import React, { useState } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { STRING } from "@constants";
import { UnitSizeRead, UnitSizeUpdate } from "@types";
import { UseManageSizesFormProps } from "./types";
import { UNIT_SIZE_FIELDS, UNIT_SIZE_INITIAL_VALUES } from "./constants";
import { UnitSizeSchema } from "./utils";
import { useManageForm } from "@hooks";
import {
  useAllUnitCategories,
  useCreateUnitSizes,
  useUpdateUnitSizes,
  useUploadUnitSizeImage,
} from "@api";
import { MediaUploader, SimpleTextField } from "@components";
import { Dropdown } from "primereact/dropdown";

export function UnitSizeForm({ editValues, onClose }: UseManageSizesFormProps) {
  const { triggerCreateUnitSize } = useCreateUnitSizes();
  const { triggerUpdateUnitSize } = useUpdateUnitSizes();
  const { triggerUploadImage, loading: uploadingImage } =
    useUploadUnitSizeImage();
  const { data: categories } = useAllUnitCategories();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { sendForm } = useManageForm<UnitSizeUpdate, UnitSizeRead>({
    createFn: (data) => triggerCreateUnitSize(data as UnitSizeRead),
    updateFn: (id, data) => triggerUpdateUnitSize({ id, payload: data }),
    successMessage: {
      create: "Unit Size successfully created",
      update: "Unit Description successfully updated",
    },
  });

  const initialValues: UnitSizeUpdate = editValues
    ? { ...editValues }
    : UNIT_SIZE_INITIAL_VALUES;

  const handleCancel = (dirty: boolean, resetForm: () => void) => {
    if (!dirty) return onClose();
    confirmDialog({
      message: STRING.UNIT_SIZE_FORM.CONFIRM_DIALOG_MESSAGE,
      header: STRING.UNIT_SIZE_FORM.CONFIRM_DIALOG_HEADER,
      icon: STRING.UNIT_SIZE_FORM.CONFIRM_DIALOG_ICON,
      acceptClassName: STRING.UNIT_SIZE_FORM.CONFIRM_DIALOG_ACCEPT_CLASS,
      accept: () => {
        resetForm();
        onClose();
      },
    });
  };

  const dynamicFields = UNIT_SIZE_FIELDS.map((field) => {
    if (field.name === "unitCategoryId") {
      return {
        ...field,
        options:
          categories?.map((loc) => ({
            label: loc.label,
            value: loc.id,
          })) || [],
      };
    }
    return field;
  });

  const handleSubmit = async (
    values: UnitSizeUpdate,
    formikHelpers: FormikHelpers<UnitSizeUpdate>,
  ) => {
    const { imageUrl, ...rest } = values;

    const savedSize = await sendForm(rest);
    if (selectedFile && savedSize?.id) {
      const updated = await triggerUploadImage({
        id: savedSize.id,
        file: selectedFile,
      });

      if (updated?.imageUrl) {
        formikHelpers.setFieldValue("imageUrl", updated.imageUrl);
      }
    }

    formikHelpers.resetForm();
    onClose();
  };

  return (
    <>
      <ConfirmDialog />
      <Formik
        initialValues={initialValues}
        validationSchema={UnitSizeSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        {({ isSubmitting, dirty, resetForm, setFieldValue, values }) => (
          <Form className="flex flex-column h-full">
            <div className="flex-1 overflow-auto px-3 bg-surface-50">
              <MediaUploader
                label={STRING.UNIT_SIZE_IMAGE_UPLOADER.TITLE}
                initialUrl={editValues?.imageUrl}
                noFileText={STRING.UNIT_SIZE_IMAGE_UPLOADER.NO_IMAGE}
                selectedText={STRING.UNIT_SIZE_IMAGE_UPLOADER.SELECTED}
                onFileSelect={(file) => setSelectedFile(file)}
              />
              {dynamicFields.map((field) => (
                <div key={field.name} className="col-12 px-1">
                  <p className="text-sm text-surface-500 mb-1 font-medium">
                    {field.label}
                  </p>

                  {field.type === "select" ? (
                    <Dropdown
                      id={field.id}
                      name={field.name}
                      value={values[field.name as keyof UnitSizeUpdate] ?? null}
                      options={
                        categories?.map((loc) => ({
                          label: loc.label,
                          value: loc.id,
                        })) || []
                      }
                      placeholder={`Select ${field.label}`}
                      className="w-full"
                      showClear
                      onChange={(e) =>
                        setFieldValue(field.name, e.value ?? null)
                      }
                    />
                  ) : (
                    <SimpleTextField
                      id={field.id}
                      name={field.name}
                      label={field.label}
                      type={field.type}
                      placeholder={field.placeholder}
                      onChange={
                        (e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(field.name, e.target.value) // valor como string
                      }
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="surface-0 border-top-1 mt-4 surface-border py-4 px-4 flex justify-content-end gap-3">
              <Button
                type="button"
                label={STRING.UNIT_SIZE_FORM.CANCEL_BUTTON}
                icon={STRING.UNIT_SIZE_FORM.CANCEL_ICON}
                className="p-button-outlined p-button-secondary"
                onClick={() => handleCancel(dirty, resetForm)}
              />
              <Button
                type="submit"
                label={
                  editValues
                    ? STRING.UNIT_SIZE_FORM.UPDATE_BUTTON
                    : STRING.UNIT_SIZE_FORM.CREATE_BUTTON
                }
                icon={
                  editValues
                    ? STRING.UNIT_SIZE_FORM.UPDATE_ICON
                    : STRING.UNIT_SIZE_FORM.CREATE_ICON
                }
                className="p-button-primary"
                loading={isSubmitting || uploadingImage}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
